

// ============================================================================
// VERSION MIGRATION SYSTEM
// ============================================================================




import { deepCloneJSON } from "@/lib/duplicate-object";
import { CURRENT_PREFERENCES_VERSION, DEFAULT_USER_DISPLAY_PREFERENCES } from "@/user-preferences/default-user-preferences";
import { UserPreferences } from "@/user-preferences/types";


/**
 * Migration function type for user preferences
 * Takes old preferences and returns migrated preferences
 */
export type PreferencesMigration = (oldPrefs: any) => UserPreferences;

/**
 * Migration registry - add new migrations here when incrementing version
 * Key is the target version, value is the migration function
 */

/**
 * v4: Persisted keys `site_rooms` / `site_locations` → `rooms` / `event_locations`.
 */
function migrate_to_v4_rename_rooms_location_table_ids(old_prefs: UserPreferences): UserPreferences {
    const pp = { ...(old_prefs.pages.pagePreferences as Record<string, unknown>) };
    if (pp.site_rooms !== undefined && pp.rooms === undefined) {
        pp.rooms = deepCloneJSON(pp.site_rooms);
        delete pp.site_rooms;
    }
    if (pp.site_locations !== undefined && pp.event_locations === undefined) {
        pp.event_locations = deepCloneJSON(pp.site_locations);
        delete pp.site_locations;
    }
    return {
        ...old_prefs,
        version: 4,
        pages: {
            ...old_prefs.pages,
            pagePreferences: pp as UserPreferences["pages"]["pagePreferences"],
        },
    };
}

/**
 * v5: Merge duplicate administration-only keys into shared `rooms` / `event_locations`, then remove legacy keys.
 */
function migrate_to_v5_consolidate_room_location_table_ids(old_prefs: UserPreferences): UserPreferences {
    const pp = { ...(old_prefs.pages.pagePreferences as Record<string, unknown>) };
    const rooms_pref = pp.rooms ?? pp.site_rooms ?? pp.administration_rooms;
    const locations_pref = pp.event_locations ?? pp.site_locations ?? pp.administration_event_locations;
    if (rooms_pref !== undefined) {
        pp.rooms = deepCloneJSON(rooms_pref);
    }
    if (locations_pref !== undefined) {
        pp.event_locations = deepCloneJSON(locations_pref);
    }
    delete pp.site_rooms;
    delete pp.site_locations;
    delete pp.administration_rooms;
    delete pp.administration_event_locations;
    return {
        ...old_prefs,
        version: 5,
        pages: {
            ...old_prefs.pages,
            pagePreferences: pp as UserPreferences["pages"]["pagePreferences"],
        },
    };
}

/**
 * v6: Drop `activeDisplayMode` / `rememberLastDisplayMode`; default mode is `availableDisplayModes[0]`.
 */
function migrate_to_v6_remove_stored_display_mode_fields(old_prefs: UserPreferences): UserPreferences {
    const pp = { ...(old_prefs.pages.pagePreferences as Record<string, Record<string, unknown>>) }
    for (const key of Object.keys(pp)) {
        const cur = pp[key]
        if (!cur || typeof cur !== "object") continue
        const { activeDisplayMode: _a, rememberLastDisplayMode: _r, ...rest } = cur
        pp[key] = rest
    }
    return {
        ...old_prefs,
        version: 6,
        pages: {
            ...old_prefs.pages,
            pagePreferences: pp as UserPreferences["pages"]["pagePreferences"],
        },
    }
}

/** v7: `calendar.weekday` → `calendar.showWeekdayNames` (weekday column labels in header). */
function migrate_to_v7_calendar_weekday_to_show_weekday_names(old_prefs: UserPreferences): UserPreferences {
    const pp = { ...(old_prefs.pages.pagePreferences as Record<string, Record<string, unknown>>) }
    for (const key of Object.keys(pp)) {
        const page = pp[key]
        if (!page || typeof page !== "object") continue
        const cal = page.calendar
        if (!cal || typeof cal !== "object") continue
        const c = { ...(cal as Record<string, unknown>) }
        if ("weekday" in c && !("showWeekdayNames" in c)) {
            c.showWeekdayNames = c.weekday
            delete c.weekday
        }
        pp[key] = { ...page, calendar: c }
    }
    return {
        ...old_prefs,
        version: 7,
        pages: {
            ...old_prefs.pages,
            pagePreferences: pp as UserPreferences["pages"]["pagePreferences"],
        },
    }
}

/** v8: `calendar.hideWeekend` → `calendar.displayWeekend` (inverted: displayWeekend = !hideWeekend). */
function migrate_to_v8_calendar_hide_weekend_to_display_weekend(old_prefs: UserPreferences): UserPreferences {
    const pp = { ...(old_prefs.pages.pagePreferences as Record<string, Record<string, unknown>>) }
    for (const key of Object.keys(pp)) {
        const page = pp[key]
        if (!page || typeof page !== "object") continue
        const cal = page.calendar
        if (!cal || typeof cal !== "object") continue
        const c = { ...(cal as Record<string, unknown>) }
        if ("hideWeekend" in c && !("displayWeekend" in c)) {
            c.displayWeekend = !Boolean(c.hideWeekend)
            delete c.hideWeekend
        }
        pp[key] = { ...page, calendar: c }
    }
    return {
        ...old_prefs,
        version: 8,
        pages: {
            ...old_prefs.pages,
            pagePreferences: pp as UserPreferences["pages"]["pagePreferences"],
        },
    }
}

export const PREFERENCES_MIGRATIONS: Record<number, PreferencesMigration> = {
    4: migrate_to_v4_rename_rooms_location_table_ids as PreferencesMigration,
    5: migrate_to_v5_consolidate_room_location_table_ids as PreferencesMigration,
    6: migrate_to_v6_remove_stored_display_mode_fields as PreferencesMigration,
    7: migrate_to_v7_calendar_weekday_to_show_weekday_names as PreferencesMigration,
    8: migrate_to_v8_calendar_hide_weekend_to_display_weekend as PreferencesMigration,
};

/**
 * Migrates user preferences from any version to the current version
 * @param preferences Raw preferences from database (might be old version)
 * @returns Migrated preferences matching current schema
 */
export function migrateUserPreferences(preferences: any): UserPreferences {
    // If no preferences or no version, return defaults
    if (!preferences || typeof preferences.version !== 'number') {
        return DEFAULT_USER_DISPLAY_PREFERENCES;
    }

    // If already current version, return as-is (with type assertion)
    if (preferences.version === CURRENT_PREFERENCES_VERSION) {
        return preferences as UserPreferences;
    }

    // Apply migrations step by step from old version to current
    let migrated = preferences;
    for (let version = preferences.version + 1; version <= CURRENT_PREFERENCES_VERSION; version++) {
        const migration = PREFERENCES_MIGRATIONS[version];
        if (!migration) {
            console.warn(`No migration found for preferences version ${version}, using defaults`);
            return DEFAULT_USER_DISPLAY_PREFERENCES;
        }
        migrated = migration(migrated);
    }

    return migrated;
}
